import React, { useEffect, useMemo, useState } from 'react';
import {
  ChildQuestionTemplate,
  CONNECTOR_OPTIONS,
  FIELD_TYPE_MAP_TRANSLATION,
  FormItem,
  FormQuestionItem,
  isPageBreak,
  MEASUREMENT_UNIT_LABEL,
  MEASUREMENT_UNIT_MAX_LENGTH,
  MEASUREMENT_UNIT_PLACEHOLDER,
  MISSING_SLOT_LABELS,
  OPERATOR_OPTIONS,
  Question,
  QUESTION_TYPE_MAP_TRANSLATION,
  StringsEntry,
} from '../../types';
import './EditQuestionModal.css';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import QuestionOptionsEditor from './QuestionOptionsEditor';
import 'react-quill/dist/quill.snow.css';
import { FaEdit } from 'react-icons/fa';
import QuestionAppearedConditionModal from './QuestionAppearedConditionModal';
import { isNonEmptyString } from '../../utils/verifyInputs';
import QuestionTextEditorModal from './QuestionTextEditorModal';
import { getPlainText } from '../../utils/InputType';
import Select from 'react-select';
import AutoChildQuestionConfig from './AutoChildQuestionConfig';
import { ChildSlotChange, computeChildChanges, SlotAction } from '../../utils/childQuestionUtils';
import {
  getFormulaValidationError,
  isFormulaRequiredButMissing,
  isOpenNumberQuestion,
} from '../../utils/formulaQuestionValidation';
import { DEFAULT_LANG } from '../../LanguageContext';
import {
  contentDirForLang,
  languageFromCode,
  PREVIEW_LANGUAGE_OPTIONS,
} from '../../utils/languageMapping';
import {
  entryFromQuestion,
  MissingSlot,
  missingSlots,
  questionInLang,
} from '../../utils/templateLoader';

/** Question types that support selectable options and therefore can have auto-generated child questions. */
const OPTIONS_CAPABLE_TYPES = ['radio', 'checkbox', 'select'];

const LOCK_WARNING_MS = 3000;

function missingSlotsSummary(slots: MissingSlot[]): string {
  const counts = new Map<string, number>();
  for (const slot of slots) {
    const label =
      (MISSING_SLOT_LABELS[slot.slot] ?? slot.slot) + (slot.childTemplateId ? ' (שאלת ילד)' : '');
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return Array.from(counts, ([label, count]) => (count > 1 ? `${label} ×${count}` : label)).join(
    ', ',
  );
}

interface EditQuestionModalProps {
  questions: FormItem[];
  questionInEdit: Question;
  /** Non-Hebrew entries keyed by lang; only set on four-language forms, which get the tabs. */
  stringsForQuestion?: Record<string, StringsEntry>;
  onClose: () => void;
  onSave: (updatedQuestion: Question, entriesByLang?: Record<string, StringsEntry>) => void;
}

const EditQuestionModal: React.FC<EditQuestionModalProps> = ({
  questions,
  questionInEdit: initialQuestion,
  stringsForQuestion,
  onClose,
  onSave,
}) => {
  const [hebrewQuestion, setHebrewQuestion] = useState<Question>(initialQuestion);
  const [formulaEnabled, setFormulaEnabled] = useState(() => Boolean(initialQuestion.formula));
  const [entries, setEntries] = useState<Record<string, StringsEntry>>(
    () => stringsForQuestion ?? {},
  );
  const [activeLang, setActiveLang] = useState(DEFAULT_LANG);
  const [showLockWarning, setShowLockWarning] = useState(false);
  const [pendingSave, setPendingSave] = useState<{
    question: Question;
    entriesByLang: Record<string, StringsEntry>;
    missing: Record<string, MissingSlot[]>;
  } | null>(null);

  const isMultiLang = stringsForQuestion !== undefined;
  const translationLangs = Object.keys(entries);
  const locked = activeLang !== DEFAULT_LANG;
  const labelSuffix = isMultiLang ? ` - ${languageFromCode(activeLang)}` : '';
  const dir = contentDirForLang(activeLang);
  // The modal JSX below edits `question`: Hebrew itself, or the active language's view of it.
  const question = locked ? questionInLang(hebrewQuestion, entries[activeLang]) : hebrewQuestion;

  const missingByLang = useMemo(() => {
    const result: Record<string, MissingSlot[]> = {};
    for (const lang of Object.keys(entries)) {
      result[lang] = missingSlots(hebrewQuestion, questionInLang(hebrewQuestion, entries[lang]));
    }
    return result;
  }, [hebrewQuestion, entries]);

  useEffect(() => {
    setHebrewQuestion(initialQuestion);
    setFormulaEnabled(Boolean(initialQuestion.formula));
  }, [initialQuestion]);

  useEffect(() => {
    if (!showLockWarning) return;
    const timeout = setTimeout(() => setShowLockWarning(false), LOCK_WARNING_MS);
    return () => clearTimeout(timeout);
  }, [showLockWarning]);

  const updateField = <K extends keyof Question>(key: K, value: Question[K]) => {
    if (!locked) {
      setHebrewQuestion((q) => ({ ...q, [key]: value }));
      return;
    }
    setEntries((prev) => ({
      ...prev,
      [activeLang]: entryFromQuestion({
        ...questionInLang(hebrewQuestion, prev[activeLang]),
        [key]: value,
      }),
    }));
  };

  // Locked controls have pointer-events: none (CSS), so the click lands on whatever is behind them.
  const warnIfLockedControl = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    const lockedControls = target.querySelectorAll(':disabled, [aria-disabled="true"]');
    const hitsLockedControl =
      target.closest('label')?.querySelector(':disabled') != null ||
      Array.from(lockedControls).some((el) => {
        const rect = el.getBoundingClientRect();
        return (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        );
      });
    if (hitsLockedControl) {
      setShowLockWarning(true);
    }
  };

  const finishSave = (savedQuestion: Question) => {
    if (!isMultiLang) {
      onSave(savedQuestion);
      onClose();
      return;
    }
    const entriesByLang: Record<string, StringsEntry> = {};
    const missing: Record<string, MissingSlot[]> = {};
    for (const lang of translationLangs) {
      const langQuestion = questionInLang(savedQuestion, entries[lang]);
      entriesByLang[lang] = entryFromQuestion(langQuestion);
      // Blank child-template text is only a warning; the children carry their own translations.
      const gaps = missingSlots(savedQuestion, langQuestion).filter((slot) => !slot.childTemplateId);
      if (gaps.length > 0) {
        missing[lang] = gaps;
      }
    }
    if (Object.keys(missing).length > 0) {
      setPendingSave({ question: savedQuestion, entriesByLang, missing });
      return;
    }
    onSave(savedQuestion, entriesByLang);
    onClose();
  };

  const prepareQuestionForSave = (questionToSave: Question): Question => {
    if (!isOpenNumberQuestion(questionToSave) || !formulaEnabled) {
      return { ...questionToSave, formula: undefined };
    }
    const trimmed = questionToSave.formula?.trim();
    return trimmed
      ? { ...questionToSave, formula: trimmed }
      : { ...questionToSave, formula: undefined };
  };

  const handleSave = () => {
    const existingChildren = questions.filter(
      (q) =>
        !isPageBreak(q) &&
        (q as FormQuestionItem).auto_generated_from_parent === initialQuestion.guid,
    ) as FormQuestionItem[];

    // Case 0: parent question type changed to a non-options type (e.g. radio → text/header/scale)
    // All auto-generated children must be removed — they rely on option conditions that no longer exist
    if (
      OPTIONS_CAPABLE_TYPES.includes(initialQuestion.question_type ?? '') &&
      !OPTIONS_CAPABLE_TYPES.includes(hebrewQuestion.question_type ?? '') &&
      existingChildren.length > 0
    ) {
      setIsTogglingOff(true);
      setAffectedSlots(
        existingChildren.map((c) => ({
          actions: ['remove'] as SlotAction[],
          templateId: c.auto_generated_from_template ?? '',
          optionGuid:
            c.appeared_conditions?.find(
              (cond) => cond.connected_question_guid === initialQuestion.guid,
            )?.values?.[0] ?? '',
          existingChild: c,
        })),
      );
      return;
    }

    // Case 1: auto-create toggled OFF — all children will be deleted
    if (
      initialQuestion.auto_create_child_questions &&
      !hebrewQuestion.auto_create_child_questions &&
      existingChildren.length > 0
    ) {
      setIsTogglingOff(true);
      setAffectedSlots(
        existingChildren.map((c) => ({
          actions: ['remove'] as SlotAction[],
          templateId: c.auto_generated_from_template ?? '',
          optionGuid:
            c.appeared_conditions?.find(
              (cond) => cond.connected_question_guid === initialQuestion.guid,
            )?.values?.[0] ?? '',
          existingChild: c,
        })),
      );
      return;
    }

    // Case 2: auto-create is on and there are existing children — check for destructive slots
    if (existingChildren.length > 0 && hebrewQuestion.auto_create_child_questions) {
      const changes = computeChildChanges(initialQuestion, hebrewQuestion, existingChildren);
      const destructive = changes.filter(
        (c) => c.actions.includes('recreate') || c.actions.includes('remove'),
      );
      if (destructive.length > 0) {
        setAffectedSlots(destructive);
        return;
      }
    }

    finishSave(prepareQuestionForSave(hebrewQuestion));
  };

  const confirmAndSave = () => {
    setAffectedSlots(null);
    setIsTogglingOff(false);
    // If the parent type is no longer options-capable, strip auto-create fields so
    // handleEditQuestion's else-branch removes the orphaned children.
    const savedQuestion = !OPTIONS_CAPABLE_TYPES.includes(hebrewQuestion.question_type ?? '')
      ? {
          ...hebrewQuestion,
          auto_create_child_questions: false,
          child_question_templates: [],
        }
      : hebrewQuestion;
    finishSave(prepareQuestionForSave(savedQuestion));
  };

  const cancelConfirm = () => {
    setAffectedSlots(null);
    setIsTogglingOff(false);
    // For Case 0 (type change) and Case 1 (toggle off), the question state already
    // reflects the destructive change — revert it so the modal shows the original value.
    setHebrewQuestion(initialQuestion);
    setFormulaEnabled(Boolean(initialQuestion.formula));
  };

  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const [isConditionModalOpen, setIsConditionModalOpen] = useState(false);
  const [affectedSlots, setAffectedSlots] = useState<ChildSlotChange[] | null>(null);
  const [isTogglingOff, setIsTogglingOff] = useState(false);

  const isDuplicateQuestionKey = (): boolean => {
    return questions
      .filter((q): q is FormQuestionItem => !isPageBreak(q))
      .some(
        (q) =>
          q.guid !== hebrewQuestion.guid &&
          q.question_key.toLowerCase() === hebrewQuestion.question_key?.toLowerCase(),
      );
  };

  const isTemplateValid = (template: ChildQuestionTemplate): boolean => {
    // Validate that options are selected
    if (!template.option_guids || template.option_guids.length === 0) {
      return false;
    }
    // Validate that at least one of text_before OR text_after is provided
    const hasTextBefore = template.text_before && template.text_before.trim() !== '';
    const hasTextAfter = template.text_after && template.text_after.trim() !== '';
    if (!hasTextBefore && !hasTextAfter) {
      return false;
    }
    if (!template.question_type) {
      return false;
    }
    // Validate template options if it's a select/checkbox/radio type
    if (['select', 'checkbox', 'radio'].includes(template.question_type)) {
      if (!template.options || template.options.length < 2) {
        return false;
      }
      if (template.options.some((opt) => !isNonEmptyString(String(opt.text)))) {
        return false;
      }
    }
    return true;
  };

  const isChildQuestionConfigValid = (q: Question): boolean => {
    if (!q.child_question_templates || q.child_question_templates.length === 0) {
      return false;
    }
    // All templates must be valid
    return q.child_question_templates.every((template) => isTemplateValid(template));
  };

  const formulaRequiredButMissing = isFormulaRequiredButMissing(hebrewQuestion, formulaEnabled);
  const allQuestionKeys = questions
    .filter((q): q is FormQuestionItem => !isPageBreak(q))
    .map((q) => q.question_key);
  const formulaValidationError = getFormulaValidationError(
    hebrewQuestion,
    formulaEnabled,
    allQuestionKeys,
  );

  const isQuestionValid = () => {
    const q = hebrewQuestion;
    if (!isNonEmptyString(q.question_key)) return false;
    if (isDuplicateQuestionKey()) return false;
    if (!isNonEmptyString(q.text)) return false;
    if (!isNonEmptyString(q.question_type)) return false;

    if (q.question_type && ['select', 'checkbox', 'radio'].includes(q.question_type)) {
      if (!q.options || q.options.length < 2) return false;
      if (q.options.some((opt) => !isNonEmptyString(opt.text))) return false;
    }

    if (q.question_type === 'scale') {
      if (
        q.scale_min_text == null ||
        q.scale_max_text == null ||
        q.scale_min_text === '' ||
        q.scale_max_text === '' ||
        q.scale_num_options == null
      )
        return false;
    }

    if (q.question_type === 'text' && q.answer_depends_on_question) {
      if (!q.dependency_question_guid || !q.dependency_operator) {
        return false;
      }
    }

    // Validate child question settings
    if (q.auto_create_child_questions && !isChildQuestionConfigValid(q)) {
      return false;
    }

    if (formulaRequiredButMissing || formulaValidationError) {
      return false;
    }

    return true;
  };

  return (
    <div className='modal-overlay'>
      <div className={`modal edit-modal${locked ? ' lang-locked' : ''}`}>
        <div className='edit-question-modal-header'>
          {isMultiLang && (
            <div className='lang-tab-row' role='tablist'>
              {PREVIEW_LANGUAGE_OPTIONS.map(({ code, label }) => {
                const slots = missingByLang[code] ?? [];
                const redCount = slots.filter((slot) => !slot.childTemplateId).length;
                const missingCount = redCount || slots.length;
                return (
                  <button
                    key={code}
                    type='button'
                    role='tab'
                    aria-selected={activeLang === code}
                    className={`lang-tab${activeLang === code ? ' lang-tab-active' : ''}`}
                    onClick={() => setActiveLang(code)}
                  >
                    {label}
                    {missingCount > 0 ? (
                      <>
                        <span className='lang-tab-count'>{missingCount}</span>
                        {redCount > 0 ? (
                          <span className='lang-tab-dot' aria-label='חסר תרגום' />
                        ) : (
                          <span
                            className='lang-tab-dot lang-tab-dot-soft'
                            aria-label='תבנית שאלת ילד ריקה'
                          />
                        )}
                      </>
                    ) : (
                      <span className='lang-tab-check' aria-label='תרגום מלא'>
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {showLockWarning && (
            <div className='structural-lock-warning' role='alert'>
              שינויים מבניים מתבצעים בלשונית עברית
            </div>
          )}
          <h3>עריכת שאלה</h3>
          <div className='header-actions'>
            <button onClick={handleSave} disabled={!isQuestionValid()} className='save-btn'>
              שמור
            </button>
            <button onClick={onClose} className='cancel-btn'>
              ביטול
            </button>
          </div>
        </div>

        <div className='modal-body layout' onClick={locked ? warnIfLockedControl : undefined}>
          <div className='form-column'>
            <div className='question-meta'>
              <label className='field-label'>מפתח השאלה</label>
              <input
                className='input-field'
                value={question.question_key}
                onChange={(e) => updateField('question_key', e.target.value)}
                disabled={locked}
              />
              {isDuplicateQuestionKey() && (
                <div className='error-message'>מפתח שאלה זה כבר קיים בטופס</div>
              )}

              <label className='field-label'>שאלת טקסט{labelSuffix}</label>
              <div className='question-preview-row'>
                <div
                  className='styled-preview-input'
                  contentEditable
                  dir={dir}
                  suppressContentEditableWarning={true}
                  onInput={(e) => updateField('text', (e.target as HTMLDivElement).innerHTML)}
                  onBlur={(e) => {
                    updateField('text', (e.target as HTMLDivElement).innerHTML);
                  }}
                  ref={(el) => {
                    if (el && el.innerHTML !== question.text) {
                      el.innerHTML = sanitizeHtml(question.text);
                    }
                  }}
                />
                <button
                  className='question-text-edit-button'
                  onClick={() => setIsTextEditorOpen(true)}
                  title='ערוך שאלה'
                >
                  <FaEdit />
                </button>
              </div>

              {isTextEditorOpen && (
                <QuestionTextEditorModal
                  initialText={question.text}
                  dir={dir}
                  onClose={() => setIsTextEditorOpen(false)}
                  onSave={(newText) => {
                    updateField('text', newText);
                    setIsTextEditorOpen(false);
                  }}
                />
              )}

              <div className='checkbox-row'>
                <input
                  type='checkbox'
                  checked={question.show_description}
                  onChange={(e) => updateField('show_description', e.target.checked)}
                  disabled={locked}
                />
                <label className='checkbox-label'>הצג הסבר</label>
              </div>

              {question.show_description && (
                <input
                  className='input-field'
                  dir={dir}
                  value={question.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder={`טקסט הסבר${labelSuffix}`}
                />
              )}

              <label className='field-label'>סוג השאלה</label>
              <select
                className='input-field'
                value={question.question_type}
                onChange={(e) => updateField('question_type', e.target.value)}
                disabled={locked}
              >
                {Object.entries(QUESTION_TYPE_MAP_TRANSLATION).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>

              {(question.question_type === 'select' || question.question_type === 'checkbox') && (
                <>
                  <div className='checkbox-row'>
                    <input
                      type='checkbox'
                      checked={question.multiple_select ?? false}
                      onChange={(e) => updateField('multiple_select', e.target.checked)}
                      disabled={locked}
                    />
                    <label className='checkbox-label'>אפשר בחירה מרובה</label>
                  </div>
                  {question.multiple_select && (
                    <div className='min-max-row'>
                      <div className='minmax-column'>
                        <label className='field-label'>מינימום אפשרויות</label>
                        <input
                          type='number'
                          className='input-field input-field-minmax'
                          value={question.min_value ?? ''}
                          onChange={(e) =>
                            updateField(
                              'min_value',
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                          disabled={locked}
                        />
                      </div>
                      <div className='minmax-column'>
                        <label className='field-label'>מקסימום אפשרויות</label>
                        <input
                          type='number'
                          className='input-field input-field-minmax'
                          value={question.max_value ?? ''}
                          onChange={(e) =>
                            updateField(
                              'max_value',
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                          disabled={locked}
                        />
                      </div>
                    </div>
                  )}
                  <QuestionOptionsEditor
                    question={question}
                    updateOption={updateField}
                    dir={dir}
                    locked={locked}
                    labelSuffix={labelSuffix}
                  />
                </>
              )}

              {question.question_type === 'radio' && (
                <QuestionOptionsEditor
                  question={question}
                  updateOption={updateField}
                  dir={dir}
                  locked={locked}
                  labelSuffix={labelSuffix}
                />
              )}

              {(question.question_type === 'select' ||
                question.question_type === 'checkbox' ||
                question.question_type === 'radio') && (
                <AutoChildQuestionConfig
                  question={question}
                  updateField={updateField}
                  dir={dir}
                  locked={locked}
                  labelSuffix={labelSuffix}
                />
              )}

              {question.question_type === 'text' && (
                <>
                  <label className='field-label'>סוג שדה</label>
                  <select
                    className='input-field'
                    value={question.field_type || 'text'}
                    onChange={(e) =>
                      updateField(
                        'field_type',
                        e.target.value as keyof typeof FIELD_TYPE_MAP_TRANSLATION,
                      )
                    }
                    disabled={locked}
                  >
                    {Object.entries(FIELD_TYPE_MAP_TRANSLATION).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>

                  <div className='checkbox-row'>
                    <input
                      type='checkbox'
                      checked={!!question.answer_depends_on_question}
                      onChange={(e) =>
                        updateField(
                          'answer_depends_on_question' as keyof Question,
                          e.target.checked,
                        )
                      }
                      disabled={locked}
                    />
                    <label className='checkbox-label'>התשובה תלויה בתשובה של שאלה אחרת</label>
                  </div>

                  {question.answer_depends_on_question && (
                    <div className='dependency-row'>
                      <div className='field-group'>
                        <label className='field-label'>אופרטור</label>
                        <Select
                          value={
                            OPERATOR_OPTIONS.find(
                              (opt) => opt.value === question.dependency_operator,
                            ) || null
                          }
                          onChange={(option) =>
                            updateField('dependency_operator', option?.value ?? '')
                          }
                          options={OPERATOR_OPTIONS.filter((opt) =>
                            ['greater_than', 'greater_equal', 'less_than', 'less_equal'].includes(
                              opt.value,
                            ),
                          )}
                          placeholder='בחר אופרטור'
                          isDisabled={locked}
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      </div>
                      <div className='field-group'>
                        <label className='field-label'>שאלה</label>
                        <Select
                          value={
                            questions
                              .filter(
                                (q): q is FormQuestionItem =>
                                  !isPageBreak(q) && q.guid === question.dependency_question_guid,
                              )
                              .map((q) => ({
                                value: q.guid,
                                label: getPlainText(q.text),
                              }))[0] || null
                          }
                          onChange={(option) =>
                            updateField('dependency_question_guid', option?.value ?? '')
                          }
                          options={questions
                            .filter((q): q is FormQuestionItem => !isPageBreak(q))
                            .map((q) => ({
                              value: q.guid,
                              label: getPlainText(q.text),
                            }))}
                          placeholder='בחר שאלה'
                          isDisabled={locked}
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {(question.field_type === 'date' || question.field_type === 'datetime') && (
                    <>
                      <div className='checkbox-row'>
                        <input
                          type='checkbox'
                          checked={!!question.date_comparison_enabled}
                          onChange={(e) => updateField('date_comparison_enabled', e.target.checked)}
                          disabled={locked}
                        />
                        <label className='checkbox-label'>השווה לתאריך היום</label>
                      </div>
                      {question.date_comparison_enabled && (
                        <div className='field-group'>
                          <Select
                            value={
                              [
                                {
                                  value: 'greater_than',
                                  label: 'גדול מהיום (עתידי)',
                                },
                                {
                                  value: 'less_than',
                                  label: 'קטן מהיום (עברי)',
                                },
                              ].find((opt) => opt.value === question.date_comparison_operator) ||
                              null
                            }
                            onChange={(option) =>
                              updateField(
                                'date_comparison_operator',
                                option?.value as 'greater_than' | 'less_than',
                              )
                            }
                            options={[
                              {
                                value: 'greater_than',
                                label: 'גדול מהיום (עתידי)',
                              },
                              { value: 'less_than', label: 'קטן מהיום (עברי)' },
                            ]}
                            placeholder='בחר סוג השוואה'
                            isDisabled={locked}
                            menuPortalTarget={document.body}
                            menuPosition='fixed'
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {question.field_type === 'number' && (
                    <>
                      <div className='min-max-row'>
                        <div className='minmax-column'>
                          <label className='field-label'>ערך מינימום</label>
                          <input
                            type='number'
                            className='input-field input-field-minmax'
                            value={question.min_value ?? ''}
                            onChange={(e) =>
                              updateField(
                                'min_value',
                                e.target.value === '' ? undefined : Number(e.target.value),
                              )
                            }
                            disabled={locked}
                          />
                        </div>
                        <div className='minmax-column'>
                          <label className='field-label'>ערך מקסימום</label>
                          <input
                            type='number'
                            className='input-field input-field-minmax'
                            value={question.max_value ?? ''}
                            onChange={(e) =>
                              updateField(
                                'max_value',
                                e.target.value === '' ? undefined : Number(e.target.value),
                              )
                            }
                            disabled={locked}
                          />
                        </div>
                      </div>

                      {/* measurement_unit: display-only in form; store undefined when cleared; max length for safety */}
                      <div className='field-group' style={{ marginTop: '12px' }}>
                        <label className='field-label'>
                          {MEASUREMENT_UNIT_LABEL}
                          {labelSuffix}
                        </label>
                        <input
                          type='text'
                          className='input-field'
                          dir={dir}
                          value={question.measurement_unit ?? ''}
                          onChange={(e) =>
                            updateField('measurement_unit', e.target.value.trim() || undefined)
                          }
                          placeholder={MEASUREMENT_UNIT_PLACEHOLDER}
                          maxLength={MEASUREMENT_UNIT_MAX_LENGTH}
                        />
                      </div>

                      <div className='checkbox-row' style={{ marginTop: '12px' }}>
                        <input
                          type='checkbox'
                          checked={formulaEnabled}
                          onChange={(e) => setFormulaEnabled(e.target.checked)}
                          disabled={locked}
                        />
                        <label className='checkbox-label'>נוסחה לחישוב</label>
                      </div>
                      {formulaEnabled && (
                        <>
                          <input
                            className='input-field'
                            value={question.formula ?? ''}
                            onChange={(e) => updateField('formula', e.target.value)}
                            placeholder='הזן נוסחה'
                            disabled={locked}
                          />
                          {formulaRequiredButMissing && (
                            <div className='error-message'>יש להזין נוסחה</div>
                          )}
                          {formulaValidationError && !formulaRequiredButMissing && (
                            <div className='error-message'>{formulaValidationError}</div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {question.question_type === 'scale' && (
                <>
                  <label className='field-label'>מספר האופציות בסקלה</label>
                  <select
                    className='input-field'
                    value={question.scale_num_options}
                    onChange={(e) => updateField('scale_num_options', Number(e.target.value))}
                    disabled={locked}
                  >
                    <option value=''>בחר מספר</option>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>

                  <label className='field-label'>ערך מינימלי{labelSuffix}</label>
                  <input
                    type='text'
                    className='input-field'
                    dir={dir}
                    value={question.scale_min_text || ''}
                    onChange={(e) => updateField('scale_min_text', e.target.value)}
                  />

                  <label className='field-label'>ערך מקסימלי{labelSuffix}</label>
                  <input
                    type='text'
                    className='input-field'
                    dir={dir}
                    value={question.scale_max_text || ''}
                    onChange={(e) => updateField('scale_max_text', e.target.value)}
                  />
                </>
              )}
            </div>
          </div>

          <div className='sidebar-column'>
            <div className='sidebar-group'>
              <label className='sidebar-field-label'>שאלת חובה</label>
              <div className='radio-group'>
                <label>
                  <input
                    type='radio'
                    name='mandatory'
                    checked={question.mandatory}
                    onChange={() => updateField('mandatory', true)}
                    disabled={locked}
                  />{' '}
                  חובה
                </label>
                <label>
                  <input
                    type='radio'
                    name='mandatory'
                    checked={!question.mandatory}
                    onChange={() => updateField('mandatory', false)}
                    disabled={locked}
                  />{' '}
                  לא
                </label>
              </div>
            </div>

            <div className='sidebar-group'>
              <label className='sidebar-field-label'>הצגת שאלה</label>
              <div className='radio-group'>
                <label>
                  <input
                    type='radio'
                    name='visibility'
                    checked={
                      question.show_question &&
                      (!question.appeared_conditions || question.appeared_conditions.length === 0)
                    }
                    onChange={() =>
                      setHebrewQuestion((q) => ({
                        ...q,
                        show_question: true,
                        appeared_conditions: [],
                      }))
                    }
                    disabled={locked}
                  />
                  תמיד
                </label>
                <label className='radio-option-with-button'>
                  <label>
                    <input
                      type='radio'
                      name='visibility'
                      checked={
                        question.appeared_conditions && question.appeared_conditions.length > 0
                      }
                      onChange={() =>
                        setHebrewQuestion((q) => ({
                          ...q,
                          show_question: true,
                          appeared_conditions: [
                            {
                              values: [],
                              operator: '',
                              connector: CONNECTOR_OPTIONS[0].value,
                              connected_question_guid: '',
                            },
                          ],
                        }))
                      }
                      disabled={locked}
                    />
                    מותנה
                  </label>
                  <button
                    type='button'
                    className='manage-conditions-btn'
                    onClick={() => setIsConditionModalOpen(true)}
                    disabled={locked}
                  >
                    ניהול תנאים
                  </button>
                </label>
                <label>
                  <input
                    type='radio'
                    name='visibility'
                    checked={!question.show_question}
                    onChange={() => updateField('show_question', false)}
                    disabled={locked}
                  />{' '}
                  לא
                </label>
              </div>
            </div>

            <QuestionAppearedConditionModal
              questions={questions}
              question={question}
              open={isConditionModalOpen}
              onSave={(conditions) => {
                updateField('appeared_conditions', conditions);
                setIsConditionModalOpen(false);
              }}
              onClose={() => setIsConditionModalOpen(false)}
            />
          </div>
        </div>
      </div>

      {affectedSlots && (
        <div className='recreation-warning-overlay'>
          <div className='recreation-warning-modal'>
            <h3>
              {isTogglingOff
                ? `כיבוי יצירת שאלות ילד אוטומטית ימחק ${affectedSlots.length} שאלות ילד`
                : `שמירה תשנה ${affectedSlots.length} שאלות ילד`}
            </h3>
            <p>
              מזהים, תנאים נוספים, שדות חובה, וכל תצורה מותאמת אישית יאבדו.
              {isMultiLang && ' התרגומים של שאלות הילד האלה יאבדו.'}
            </p>
            <details className='recreation-warning-details'>
              <summary>הצג פרטים ({affectedSlots.length} שאלות)</summary>
              <ul className='recreation-warning-list'>
                {affectedSlots.map((slot) => {
                  const action = slot.actions.includes('recreate') ? 'recreate' : 'remove';
                  const child = slot.existingChild;
                  const extraConditions =
                    (child?.appeared_conditions?.length ?? 0) +
                    (child?.question_conditions?.length ?? 0);
                  return (
                    <li key={`${slot.templateId}_${slot.optionGuid}`}>
                      <span className={`slot-action-badge slot-action-${action}`}>
                        {action === 'recreate' ? 'יצירה מחדש' : 'הסרה'}
                      </span>
                      <span className='slot-question-text'>{child?.text ?? '--'}</span>
                      <span className='slot-question-key'>({child?.question_key ?? ''})</span>
                      {extraConditions > 1 && (
                        <span className='slot-warning-detail'>{extraConditions} תנאים</span>
                      )}
                      {child?.mandatory && <span className='slot-warning-detail'>חובה</span>}
                    </li>
                  );
                })}
              </ul>
            </details>
            <div className='recreation-warning-buttons'>
              <button className='cancel-btn' onClick={cancelConfirm}>
                ביטול
              </button>
              <button className='warning-btn' onClick={confirmAndSave}>
                המשך ושמור
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingSave && (
        <div className='recreation-warning-overlay'>
          <div className='recreation-warning-modal' role='dialog' aria-modal='true'>
            <h3>חסר תרגום בשאלה הזו</h3>
            <p>אפשר לשמור את השאלה עכשיו, אבל שמירת הטופס תיחסם עד שהתרגום יושלם.</p>
            <ul className='recreation-warning-list'>
              {Object.entries(pendingSave.missing).map(([lang, slots]) => (
                <li key={lang}>
                  <strong>{languageFromCode(lang)}:</strong> {missingSlotsSummary(slots)}
                </li>
              ))}
            </ul>
            <div className='recreation-warning-buttons'>
              <button className='cancel-btn' onClick={() => setPendingSave(null)}>
                חזור
              </button>
              <button
                className='save-btn'
                onClick={() => {
                  onSave(pendingSave.question, pendingSave.entriesByLang);
                  onClose();
                }}
              >
                שמור בכל זאת
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditQuestionModal;
